import type React from 'react';
import { Link } from 'react-router-dom';
import { PageTitle } from '@/hooks/usePageTitle';

/* ==========================================================================
   SUPPORT — where to get help, for the website and the Showliner app for
   iPhone. App Store Connect needs a support URL, and this is it. Keep the
   in-app names below (Account, Notifications, Delete Account) matching the
   app's own labels.
   ========================================================================== */

const SUPPORT_EMAIL = 'hello@showliner.io';

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div>
    <h2 className="font-heading text-xl font-semibold text-grey-900 mb-3">{title}</h2>
    <div className="text-grey-700 leading-relaxed space-y-3">{children}</div>
  </div>
);

const Support: React.FC = () => (
  <>
    <PageTitle title="Support" />

    <section>
      <div className="mx-auto max-w-3xl px-4 sm:px-8 page-top">
        <h1 className="font-display text-[clamp(2.25rem,5vw,3.5rem)] leading-[1.02] tracking-tight text-ink">
          Support
        </h1>
        <p className="mt-5 text-lg text-grey-700 leading-relaxed">
          Email{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="underline hover:text-ink">{SUPPORT_EMAIL}</a>.
          Tell us the show and the workspace you were working in, and what you expected to happen.
          A screenshot helps.
        </p>
      </div>
    </section>

    <section>
      <div className="mx-auto max-w-3xl px-4 sm:px-8 section pt-0 space-y-10">
        <Section title="The Showliner app for iPhone">
          <p>
            The app uses the same account as the website. Sign in with the email, Apple or Google
            account you use there. Your shows, offers, settlements and the advance are the same in
            both places.
          </p>
          <p>
            <strong>Notifications not arriving?</strong> Check that notifications are on for
            Showliner in your iPhone's Settings, then open Account, then Notifications in the app to
            see which ones you get and when your quiet hours are.
          </p>
          <p>
            <strong>Something looks out of date?</strong> Pull down on the list to refresh. Without
            a signal the app shows what you last saw and says so at the top.
          </p>
        </Section>

        <Section title="Your account">
          <p>
            To sign out, open Account and tap Sign Out. To delete your account, open Account and tap
            Delete Account, or use your account settings on the website. We email you a link to
            restore it for 30 days. Our{' '}
            <Link to="/privacy" className="underline hover:text-ink">privacy policy</Link>{' '}
            says what is kept and what is erased.
          </p>
        </Section>

        <Section title="Text messages">
          <p>
            Reply STOP to any Showliner text to stop them, or HELP for help. More on the{' '}
            <Link to="/sms" className="underline hover:text-ink">text messages page</Link>.
          </p>
        </Section>

        <Section title="Terms and privacy">
          <p>
            <Link to="/terms" className="underline hover:text-ink">Terms of service</Link>
            {' · '}
            <Link to="/privacy" className="underline hover:text-ink">Privacy policy</Link>
          </p>
        </Section>
      </div>
    </section>
  </>
);

export default Support;
